import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobDescription from '../../../components/JobDescription/JobDescription';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface DocumentsPageProps {
    step?: string;
    gt?: GettextInterface;
}

interface DocumentFile {
    id: string;
    file: File;
    type: 'cover-letter' | 'portfolio' | 'certificate' | 'other';
    uploadProgress: number;
}

const DocumentsPage: React.FunctionComponent<DocumentsPageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();
    const [documents, setDocuments] = useState<DocumentFile[]>([]);
    const [uploadError, setUploadError] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (
        files: FileList | null,
        documentType: DocumentFile['type'] = 'other',
    ): void => {
        if (!files) return;

        const newDocuments: DocumentFile[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Validate file type
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'text/plain',
                'image/jpeg',
                'image/png',
            ];

            if (!allowedTypes.includes(file.type)) {
                setUploadError('Please select supported file types: PDF, DOC, DOCX, TXT, JPG, PNG');
                continue;
            }

            // Validate file size (10MB limit)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError('File size must be less than 10MB');
                continue;
            }

            newDocuments.push({
                id: `doc_${Date.now()}_${i}`,
                file,
                type: documentType,
                uploadProgress: 100, // Mock: instant upload
            });
        }

        if (newDocuments.length > 0) {
            setUploadError('');
            setDocuments((prev) => [...prev, ...newDocuments]);
        }
    };

    const handleDragOver = (e: React.DragEvent): void => {
        e.preventDefault();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent): void => {
        e.preventDefault();
        setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent): void => {
        e.preventDefault();
        setDragActive(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        handleFileSelect(e.target.files);
    };

    const handleFileUploadClick = (): void => {
        fileInputRef.current?.click();
    };

    const removeDocument = (id: string): void => {
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    };

    const handleBack = (): void => {
        void goBack();
    };

    const handleContinue = (): void => {
        void goForward();
    };

    const handleSkip = (): void => {
        void goForward();
    };

    const getFileIcon = (file: File): string => {
        if (file.type.includes('pdf')) return '📄';
        if (file.type.includes('word')) return '📝';
        if (file.type.includes('image')) return '🖼️';
        if (file.type.includes('text')) return '📃';
        return '📎';
    };

    const formatFileSize = (bytes: number): string => {
        const mb = bytes / (1024 * 1024);
        return mb > 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
    };

    return (
        <PageTransition pageKey="documents">
            <Helmet>
                <title>Supporting Documents - Indeed</title>
                <meta
                    name="description"
                    content="Upload your cover letter and supporting documents to strengthen your job application."
                />
                <meta
                    name="keywords"
                    content="cover letter, portfolio, documents, job application, Indeed, supporting materials"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Documents Upload */}
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        Supporting Documents
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        Upload additional documents to support your application. This step is
                        optional but recommended.
                    </Text>

                    {/* Upload Area */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Box
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={handleFileUploadClick}
                            sx={{
                                border: '2px dashed',
                                borderColor: dragActive ? 'primary.default' : 'border.subtle',
                                borderRadius: 2,
                                padding: 8,
                                textAlign: 'center',
                                backgroundColor: dragActive
                                    ? 'primary.subtle'
                                    : 'background.subtle',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: 'primary.default',
                                    backgroundColor: 'primary.subtle',
                                },
                            }}
                        >
                            <Box sx={{ marginBlockEnd: 4, fontSize: '48px' }}>📁</Box>
                            <Text
                                sx={{
                                    color: 'foreground.primary',
                                    marginBlockEnd: 3,
                                    fontWeight: 'semibold',
                                }}
                            >
                                Upload Supporting Documents
                            </Text>
                            <Text sx={{ color: 'foreground.secondary', marginBlockEnd: 3 }}>
                                Drag and drop your files here, or click to browse
                            </Text>
                            <Button variant="secondary">Choose Files</Button>
                        </Box>

                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                            onChange={handleFileInputChange}
                            style={{ display: 'none' }}
                        />

                        {/* Error message */}
                        {uploadError && (
                            <Text
                                sx={{
                                    color: 'danger.default',
                                    fontSize: 1,
                                    marginBlockStart: 3,
                                    textAlign: 'center',
                                }}
                            >
                                {uploadError}
                            </Text>
                        )}
                    </Box>

                    {/* Uploaded Documents List */}
                    {documents.length > 0 && (
                        <Box sx={{ marginBlockEnd: 6 }}>
                            <Heading
                                as="h3"
                                level={4}
                                sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}
                            >
                                Uploaded Documents ({documents.length})
                            </Heading>
                            {documents.map((doc) => (
                                <Box
                                    key={doc.id}
                                    data-dd-action-name="uploaded document"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: 3,
                                        border: '1px solid',
                                        borderColor: 'border.subtle',
                                        borderRadius: 2,
                                        marginBlockEnd: 2,
                                        backgroundColor: 'background.default',
                                    }}
                                >
                                    <Text sx={{ fontSize: '24px', marginInlineEnd: 3 }}>
                                        {getFileIcon(doc.file)}
                                    </Text>
                                    <Box sx={{ flex: 1 }}>
                                        <Text sx={{ fontWeight: 'semibold', marginBlockEnd: 1 }}>
                                            {doc.file.name}
                                        </Text>
                                        <Text sx={{ color: 'foreground.secondary', fontSize: 1 }}>
                                            {formatFileSize(doc.file.size)} •{' '}
                                            {doc.type.replace('-', ' ')}
                                        </Text>
                                    </Box>
                                    <Button
                                        variant="tertiary"
                                        onClick={() => removeDocument(doc.id)}
                                        sx={{ marginInlineStart: 2 }}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Document Types Guide */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}
                        >
                            Recommended Documents
                        </Heading>
                        <Box as="ul" sx={{ paddingInlineStart: 4 }}>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                <strong>Cover Letter:</strong> Personalized letter explaining your
                                interest and qualifications
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                <strong>Portfolio:</strong> Work samples, case studies, or project
                                examples
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                <strong>Certifications:</strong> Professional certifications or
                                training certificates
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                <strong>References:</strong> Professional reference letters or
                                contact information
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                <strong>Transcripts:</strong> Academic transcripts (if relevant to
                                the position)
                            </Text>
                        </Box>
                    </Box>

                    {/* File Guidelines */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ color: 'foreground.primary', marginBlockEnd: 3 }}
                        >
                            File Guidelines
                        </Heading>
                        <Box as="ul" sx={{ paddingInlineStart: 4 }}>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Maximum file size: 10MB per file
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Use clear, descriptive file names
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Ensure documents are up-to-date and relevant
                            </Text>
                        </Box>
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 3,
                            marginBlockStart: 8,
                        }}
                    >
                        <Box>
                            {canGoBack && (
                                <Button variant="secondary" onClick={handleBack}>
                                    Back
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Button variant="tertiary" onClick={handleSkip}>
                                Skip for Now
                            </Button>
                            <Button variant="primary" onClick={handleContinue}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Right Section - Job Description */}
                <JobDescription />
            </Box>
        </PageTransition>
    );
};

export default DocumentsPage;
